/**
 * The UI's view of a saved alert. The wire shapes live in the generated
 * OpenAPI types; `api.ts` assembles them — an alert row, its runs, and the
 * read state derived from them — into these.
 */
import type { components } from "../api/openapi/company-router";

export type AlertPeriodicity = components["schemas"]["AlertPeriodicity"];

/** How much data one evaluation looks at, always relative to the run time. */
export type TimeWindow = { days: number };

export const WINDOW_PRESETS = [7, 14, 30, 365];

/** The default cadence, and the only one the copy has a single word for. */
export const WEEKLY_DAYS = 7;
export const SCHEDULE_PRESETS = [WEEKLY_DAYS, 14, 30];

/** What the context selectors produce: whose work, which processes, how far back. */
export type WatchlistScope = {
  userIds: string[];
  /** Empty means every discovered process unless allProcesses is explicitly false. */
  processSlugs: string[];
  /** Distinguishes an explicitly cleared editor from legacy saved All scopes. */
  allProcesses?: boolean;
  window: TimeWindow;
};

export const hasProcessSelection = (scope: WatchlistScope): boolean =>
  scope.processSlugs.length > 0 || scope.allProcesses !== false;

import type { ClankPublicMessage } from "../api";

/** How often an alert re-runs. */
export type Schedule = { everyDays: number };

type AlertRunBase = {
  id: string;
  ranAt: string;
};

/** One completed evaluation of an alert. */
export type AlertRun =
  | (AlertRunBase & {
      status: "success";
      /** Full markdown report the model produced for this run. */
      response: string;
      /**
       * The answer at a glance — a word, number, or few-word fragment — shown in
       * the alert list for the latest run.
       */
      headline: string;
      /** Compact recap handed to a chat started from this alert as its context. */
      summary: string;
    })
  | (AlertRunBase & {
      status: "failed";
      /** Structured public message for frontend localization. */
      error: ClankPublicMessage;
    });

/**
 * A saved prompt evaluated on a schedule — covers both "alert me when" and
 * "track this KPI"; the two are the same stored thing until they need to
 * render differently.
 */
export type Alert = {
  id: string;
  prompt: string;
  /**
   * Short label the alert is listed under, written by the review endpoint when
   * it was created. Empty on alerts saved without one — a failed review, or a
   * row older than titles — which are listed by their prompt instead.
   */
  title: string;
  scope: WatchlistScope;
  /**
   * IANA timezone anchoring the scope window's day borders, stamped from the
   * creating browser. Not edited in the UI, but kept so updates preserve it.
   */
  timezone: string;
  periodicity: AlertPeriodicity;
  /** Only `periodic` alerts have one. */
  schedule: Schedule | null;
  enabled: boolean;
  /** A run exists that the creator has not opened yet. */
  unread: boolean;
  createdAt: string;
  /**
   * When the question, scope or cadence last changed — equal to `createdAt`
   * on an alert nobody has edited. Runs older than this answered a different
   * question, which is what the detail page's divider says.
   */
  editedAt: string;
  /** Completed runs, newest first. */
  runs: AlertRun[];
};

export type AlertDraft = {
  prompt: string;
  title: string;
  scope: WatchlistScope;
  schedule: Schedule;
};

/**
 * What the model made of a draft alert, before it is saved.
 *
 * An alert is evaluated with nobody present, so anything ambiguous in it is
 * ambiguous forever; this is the one moment the model can ask. `questions`
 * empty *is* the verdict that the alert is ready — there is no separate flag
 * to fall out of step with it.
 */
export type AlertReview = {
  /** Always written, and saved as-is on the ready path. */
  title: string;
  /** The question tightened: offered as a replacement, never applied for you. */
  prompt: string;
  /** The reviewer's best complete scope, constrained to values visible to the author. */
  scope: AlertReviewScopeProposal;
  /** What has to be settled first. Empty means nothing has. */
  questions: AlertReviewQuestion[];
};

export type AlertReviewScopeProposal = {
  userIds: string[];
  processSlugs: string[];
  windowDays: number;
  scheduleEveryDays: number;
};

export type AlertReviewOption = {
  label: string;
  description: string;
  /** Self-contained answer sent back to the reviewer after this batch. */
  answer: string;
  recommended: boolean;
};

export type AlertReviewQuestion = {
  id: string;
  question: string;
  dimension: "people" | "processes" | "window" | "schedule" | "definition" | string;
  options: AlertReviewOption[];
};

/** Which context controls are authored choices rather than untouched defaults. */
export type ScopeTouched = {
  people: boolean;
  processes: boolean;
  window: boolean;
  schedule: boolean;
};

export type AlertReviewContext = {
  scope: WatchlistScope;
  schedule: Schedule;
  userOptions: Array<{ id: string; label: string }>;
  processOptions: Array<{ id: string; label: string }>;
  touched: ScopeTouched;
};
